import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { roomsAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import './JoinRoom.css';

const JoinRoom = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [roomInfo, setRoomInfo] = useState(null);
  const [joining, setJoining] = useState(false);

  useEffect(() => {
    loadInvitation();
  }, [token]);

  const loadInvitation = async () => {
    try {
      setLoading(true);
      const response = await roomsAPI.getInvitationInfo(token);
      setRoomInfo(response);
      setError('');
    } catch (err) {
      setError(err.message || 'Invalid or expired invitation link');
    } finally {
      setLoading(false);
    }
  };

  const handleJoin = async () => {
    try {
      setJoining(true);
      await roomsAPI.joinViaInvitation(token);
      // Check if user is the host - if so, join directly
      // Handle both API response (ownerId) and socket response (hostId)
      const isHost = roomInfo?.room?.ownerId === user?.id || roomInfo?.room?.hostId === user?.id;
      if (isHost) {
        // Host can join directly
        navigate(`/room/${roomInfo.room.id}`);
      } else {
        // Redirect to waiting room instead of directly joining
        navigate(`/rooms/${roomInfo.room.id}/waiting`);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setJoining(false);
    }
  };

  if (loading) {
    return (
      <div className="join-room-page">
        <div className="join-room-card">
          <div className="loading">Chargement...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="join-room-page">
        <div className="join-room-card">
          <div className="error-icon">❌</div>
          <h2>Invitation invalide</h2>
          <p>{error}</p>
          <button className="btn-primary" onClick={() => navigate('/rooms')}>
            Retour aux Rooms
          </button>
        </div>
      </div>
    );
  }

  // If user is not logged in, show message to login
  if (!user) {
    return (
      <div className="join-room-page">
        <div className="join-room-card">
          <div className="invitation-icon">📧</div>
          <h2>Invitation à rejoindre une Room</h2>
          <div className="room-preview">
            <h3>{roomInfo?.room?.name}</h3>
            <p>{roomInfo?.room?.description || 'Aucune description'}</p>
          </div>
          <p className="login-message">
            Vous devez être connecté pour rejoindre cette room.
          </p>
          <div className="auth-buttons">
            <button className="btn-primary" onClick={() => navigate('/login', { state: { from: `/rooms/join/${token}` } })}>
              Se connecter
            </button>
            <button className="btn-secondary" onClick={() => navigate('/register', { state: { from: `/rooms/join/${token}` } })}>
              S'inscrire
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="join-room-page">
      <div className="join-room-card">
        <div className="invitation-icon">📧</div>
        <h2>Invitation à rejoindre une Room</h2>
        
        <div className="room-preview">
          <div className="room-name">{roomInfo?.room?.name}</div>
          {roomInfo?.room?.description && (
            <p className="room-description">{roomInfo.room.description}</p>
          )}
          <div className="invite-role">
            Rôle proposé: <span className="role-badge">{roomInfo?.role === 'admin' ? 'Administrateur' : 'Membre'}</span>
          </div>
        </div>

        <div className="user-info">
          <p>Vous êtes connecté en tant que:</p>
          <strong>{user.name}</strong>
          <span>({user.email})</span>
        </div>

        {error && <div className="error-message">{error}</div>}

        <div className="join-actions">
          <button 
            className="btn-primary btn-large" 
            onClick={handleJoin}
            disabled={joining}
          >
            {joining ? 'Connexion...' : 'Rejoindre la Room'}
          </button>
          <button 
            className="btn-secondary" 
            onClick={() => navigate('/rooms')}
          >
            Annuler
          </button>
        </div>
      </div>
    </div>
  );
};

export default JoinRoom;
